
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Tooltip,
    alpha,
    useTheme,
    Switch,
    FormControlLabel,
    TablePagination,
    Chip,
    InputAdornment,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Skeleton
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    LocationOn as LocationOnIcon,
    Warning as WarningIcon,
    Search as SearchIcon,
    MoreVert as MoreVertIcon,
    Visibility as ViewIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { constituencyService } from '../service/constituencyService';
import SuccessNotification from '../components/SuccessNotification';

export default function Constituency() {
    const theme = useTheme();
    const [constituencies, setConstituencies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formLoading, setFormLoading] = useState(false);

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');

    // Modal state
    const [open, setOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        constituencyName: '',
        isActive: true
    });

    // Menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [viewModalOpen, setViewModalOpen] = useState(false);

    // Notification State
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        setLoading(true);
        const timer = setTimeout(() => {
            fetchConstituencies();
        }, 500);
        return () => clearTimeout(timer);
    }, [page, rowsPerPage, searchTerm]);

    const fetchConstituencies = async () => {
        setLoading(true);
        try {
            // Ensure loading lasts for at least 1 second for smooth skeleton transition
            const [response] = await Promise.all([
                constituencyService.getConstituencies(page + 1, rowsPerPage, searchTerm),
                new Promise(resolve => setTimeout(resolve, 1000))
            ]);
            if (response.success) {
                if (response.data && response.data.data) {
                    setConstituencies(response.data.data);
                    setTotalCount(response.data.totalRecords || 0);
                } else {
                    const data = response.data || [];
                    setConstituencies(data);
                    setTotalCount(data.length);
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpen = () => {
        setIsEditing(false);
        setFormData({ constituencyName: '', isActive: true });
        setOpen(true);
    };

    const handleEditOpen = (item) => {
        setIsEditing(true);
        setSelectedId(item.id);
        setFormData({
            constituencyName: item.constituencyName || '',
            isActive: item.isActive !== undefined ? item.isActive : true
        });
        setOpen(true);
    };

    const handleViewOpen = (item) => {
        setSelectedItem(item);
        setViewModalOpen(true);
    };

    const handleMenuOpen = (event, item) => {
        setAnchorEl(event.currentTarget);
        setSelectedItem(item);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedItem(null);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedId(null);
    };

    const handleSubmit = async () => {
        if (!formData.constituencyName.trim()) return;
        setFormLoading(true);
        try {
            let response = isEditing ?
                await constituencyService.updateConstituency(selectedId, formData.constituencyName, formData.isActive) :
                await constituencyService.addConstituency(formData.constituencyName);

            if (response.success) {
                fetchConstituencies();
                handleClose();
                setNotification({ open: true, message: 'Saved successfully!', severity: 'success' });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!selectedId) return;
        setFormLoading(true);
        try {
            const response = await constituencyService.deleteConstituency(selectedId);
            if (response.success) {
                fetchConstituencies();
                setDeleteModalOpen(false);
                setNotification({ open: true, message: 'Deleted successfully!', severity: 'success' });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setFormLoading(false);
            setSelectedId(null);
        }
    };

    return (
        <Box sx={{ pb: 5 }}>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: { xs: 3, sm: 4 },
                p: { xs: 2, sm: 3 },
                borderRadius: 4,
                bgcolor: 'white',
                boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
                gap: 2
            }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b', letterSpacing: '-0.5px', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>Constituency</Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpen}
                    sx={{
                        borderRadius: 2.5,
                        px: 3,
                        py: 1.2,
                        width: { xs: '100%', sm: 'auto' },
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        background: 'primary.main',
                        boxShadow: `0 4px 14px 0 ${alpha(theme.palette.secondary.main, 0.39)}`,
                        '&:hover': {
                            background: 'primary.dark',
                            boxShadow: `0 6px 20px ${alpha(theme.palette.secondary.main, 0.23)}`
                        }
                    }}
                >
                    Add Constituency
                </Button>
            </Box>

            <Paper sx={{
                borderRadius: 4,
                overflow: 'hidden',
                boxShadow: '0 10px 30px 0 rgba(0,0,0,0.04)',
                border: '1px solid rgba(226, 232, 240, 0.8)',
                mb: 4
            }}>
                <Box sx={{ p: { xs: 2, sm: 3 }, borderBottom: '1px solid', borderColor: '#f1f5f9', bgcolor: '#ffffff' }}>
                    <TextField
                        fullWidth
                        placeholder="Search constituencies..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                                </InputAdornment>
                            ),
                            sx: {
                                bgcolor: '#f8fafc',
                                borderRadius: 3,
                                '& fieldset': { border: 'none' },
                                '&:hover': { bgcolor: '#f1f5f9' },
                                transition: 'all 0.2s'
                            }
                        }}
                    />
                </Box>
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="medium" sx={{ minWidth: 600 }}>
                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase', py: 2 }}>Actions</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>ID</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Constituency Name</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase', textAlign: 'center' }}>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                Array.from(new Array(rowsPerPage)).map((_, index) => (
                                    <TableRow key={`skeleton-${index}`}>
                                        <TableCell><Skeleton variant="circular" width={32} height={32} /></TableCell>
                                        <TableCell><Skeleton width="40px" /></TableCell>
                                        <TableCell><Skeleton width="200px" /></TableCell>
                                        <TableCell align="center"><Skeleton width="80px" height={32} sx={{ margin: '0 auto', borderRadius: 1.5 }} /></TableCell>
                                    </TableRow>
                                ))
                            ) : (constituencies.length > rowsPerPage ? constituencies.slice(page * rowsPerPage, (page + 1) * rowsPerPage) : constituencies).map((item) => (
                                <TableRow key={item.id} hover sx={{ '&:hover': { bgcolor: 'rgba(241, 245, 249, 0.5) !important' }, transition: 'all 0.2s' }}>
                                    <TableCell>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleMenuOpen(e, item)}
                                            sx={{ color: '#64748b' }}
                                        >
                                            <MoreVertIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                    <TableCell sx={{ color: '#94a3b8', fontWeight: 600 }}>#{item.id}</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>{item.constituencyName}</TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={item.isActive ? 'Active' : 'Inactive'}
                                            size="small"
                                            sx={{
                                                fontWeight: 700,
                                                borderRadius: 1.5,
                                                fontSize: '0.75rem',
                                                bgcolor: item.isActive ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.grey[500], 0.1),
                                                color: item.isActive ? theme.palette.primary.main : theme.palette.grey[700],
                                                border: 'none'
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
                <DialogTitle>{isEditing ? 'Edit' : 'Add'} Constituency</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <TextField label="Name" fullWidth value={formData.constituencyName} onChange={(e) => setFormData({ ...formData, constituencyName: e.target.value })} />
                        <FormControlLabel control={<Switch checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />} label="Active" />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={formLoading}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
                <DialogContent sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant="h6">Delete Constituency?</Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3, justifyContent: 'center' }}>
                    <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm}>Delete</Button>
                </DialogActions>
            </Dialog>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: { minWidth: 150, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }
                }}
            >
                <MenuItem onClick={() => { handleMenuClose(); handleViewOpen(selectedItem); }}>
                    <ListItemIcon><ViewIcon fontSize="small" color="info" /></ListItemIcon>
                    <ListItemText primary="View " />
                </MenuItem>
                <MenuItem onClick={() => { handleMenuClose(); handleEditOpen(selectedItem); }}>
                    <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
                    <ListItemText primary="Edit" />
                </MenuItem>
                <MenuItem onClick={() => { handleMenuClose(); setSelectedId(selectedItem.id); setDeleteModalOpen(true); }} sx={{ color: 'error.main' }}>
                    <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText primary="Delete" />
                </MenuItem>
            </Menu>

            {/* View Constituency Modal */}
            <Dialog
                open={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Constituency Details
                    <IconButton onClick={() => setViewModalOpen(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedItem && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 1 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Constituency Name</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>{selectedItem.constituencyName}</Typography>
                            </Box>

                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>ID</Typography>
                                <Typography variant="body1">#{selectedItem.id}</Typography>
                            </Box>

                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Status</Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    <Chip
                                        label={selectedItem.isActive ? 'Active' : 'Inactive'}
                                        color={selectedItem.isActive ? 'success' : 'default'}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontWeight: 700 }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setViewModalOpen(false)} variant="contained" fullWidth sx={{ borderRadius: 2 }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <SuccessNotification open={notification.open} message={notification.message} onClose={() => setNotification({ ...notification, open: false })} />
        </Box>
    );
}
